import { Currency } from '@kodiak-finance/sdk-core'
import WETH_ABI from 'abis/weth.json'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useCallback, useMemo } from 'react'
import { isUserRejection } from 'utils/userRejection'
import { Address } from 'viem'
import { useSimulateContract } from 'wagmi'

import { REFETCH_POLICY_FAST } from '../config/RefetchPolicies'
import { WRAPPED_NATIVE_CURRENCY } from '../constants/tokens'
import { useCurrencyBalance } from '../state/connection/hooks'
import { useTransactionAdder } from '../state/transactions/hooks'
import { TransactionType } from '../state/transactions/types'

export enum WrapType {
  NOT_APPLICABLE,
  WRAP,
  UNWRAP,
}

const NOT_APPLICABLE = { wrapType: WrapType.NOT_APPLICABLE }

enum WrapInputError {
  NO_ERROR, // must be equal to 0 so all other errors are truthy
  ENTER_NATIVE_AMOUNT,
  ENTER_WRAPPED_AMOUNT,
  INSUFFICIENT_NATIVE_BALANCE,
  INSUFFICIENT_WRAPPED_BALANCE,
}

export function WrapErrorText({ wrapInputError }: { wrapInputError: WrapInputError }) {
  const native = useNativeCurrency()
  const wrapped = native?.wrapped

  switch (wrapInputError) {
    case WrapInputError.NO_ERROR:
      return null
    case WrapInputError.ENTER_NATIVE_AMOUNT:
      return <>Enter {native?.symbol} amount</>
    case WrapInputError.ENTER_WRAPPED_AMOUNT:
      return <>Enter {wrapped?.symbol} amount</>

    case WrapInputError.INSUFFICIENT_NATIVE_BALANCE:
      return <>Insufficient {native?.symbol} balance</>
    case WrapInputError.INSUFFICIENT_WRAPPED_BALANCE:
      return <>Insufficient {wrapped?.symbol} balance</>
  }
}

/**
 * Given the selected input and output currency, return a wrap callback
 * @param inputCurrency the selected input currency
 * @param outputCurrency the selected output currency
 * @param typedValue the user input value
 */
export default function useWrapCallback(
  inputCurrency: Currency | undefined | null,
  outputCurrency: Currency | undefined | null,
  typedValue: string | undefined
): {
  wrapType: WrapType
  execute?: undefined | (() => Promise<string>)
  inputError?: WrapInputError
  isPending?: boolean
} {
  const { chainId, account } = useConnection()
  const balance = useCurrencyBalance(account ?? undefined, inputCurrency ?? undefined, REFETCH_POLICY_FAST)
  const inputAmount = useMemo(
    () => tryParseCurrencyAmount(typedValue, inputCurrency ?? undefined),
    [inputCurrency, typedValue]
  )
  const addTransaction = useTransactionAdder()

  // Determine wrap type and WETH address
  const weth = chainId ? WRAPPED_NATIVE_CURRENCY[chainId] : undefined
  const wethAddress = weth?.address as Address | undefined

  const isWrapping = inputCurrency?.isNative && weth && outputCurrency && weth.equals(outputCurrency)
  const isUnwrapping = weth && inputCurrency && weth.equals(inputCurrency) && outputCurrency?.isNative
  const hasInputAmount = Boolean(inputAmount?.greaterThan('0'))
  const sufficientBalance = inputAmount && balance && !balance.lessThan(inputAmount)

  // Simulate deposit (wrap)
  const { data: depositSimulation } = useSimulateContract({
    abi: WETH_ABI,
    address: wethAddress,
    functionName: 'deposit',
    value: inputAmount ? BigInt(inputAmount.quotient.toString()) : undefined,
    chainId,
    query: {
      enabled: Boolean(isWrapping && hasInputAmount && sufficientBalance && wethAddress && account),
    },
  })

  // Simulate withdraw (unwrap)
  const { data: withdrawSimulation } = useSimulateContract({
    abi: WETH_ABI,
    address: wethAddress,
    functionName: 'withdraw',
    args: inputAmount ? [BigInt(inputAmount.quotient.toString())] : undefined,
    chainId,
    query: {
      enabled: Boolean(isUnwrapping && hasInputAmount && sufficientBalance && wethAddress && account),
    },
  })

  const { writeContractAsync, isPending: isWritePending } = useWriteContract()

  const execute = useCallback(
    async (isWrap: boolean) => {
      if (!chainId || !inputAmount || !account) {
        throw new Error('Missing dependencies')
      }

      if (!weth) throw new Error('WETH not found')

      const simulation = isWrap ? depositSimulation : withdrawSimulation

      if (!simulation?.request) {
        throw new Error('Transaction simulation failed')
      }

      try {
        const hash = await writeContractAsync(simulation.request)

        addTransaction({ hash } as any, {
          type: TransactionType.WRAP,
          unwrapped: !isWrap,
          currencyAmountRaw: inputAmount?.quotient.toString(),
          chainId,
        })

        return hash
      } catch (error: any) {
        // Handle user rejection separately from actual errors
        if (isUserRejection(error)) {
          throw new Error('User rejected transaction')
        }

        console.error(isWrap ? 'Could not deposit' : 'Could not withdraw', error)
        throw error
      }
    },
    [chainId, inputAmount, account, weth, depositSimulation, withdrawSimulation, writeContractAsync, addTransaction]
  )

  return useMemo(() => {
    if (!chainId || !inputCurrency || !outputCurrency || !weth) return NOT_APPLICABLE

    if (isWrapping) {
      return {
        wrapType: WrapType.WRAP,
        isPending: isWritePending,
        execute: account && sufficientBalance && inputAmount ? async () => execute(true) : undefined,
        inputError: sufficientBalance
          ? undefined
          : hasInputAmount
          ? WrapInputError.INSUFFICIENT_NATIVE_BALANCE
          : WrapInputError.ENTER_NATIVE_AMOUNT,
      }
    } else if (isUnwrapping) {
      return {
        wrapType: WrapType.UNWRAP,
        isPending: isWritePending,
        execute: account && sufficientBalance && inputAmount ? async () => execute(false) : undefined,
        inputError: sufficientBalance
          ? undefined
          : hasInputAmount
          ? WrapInputError.INSUFFICIENT_WRAPPED_BALANCE
          : WrapInputError.ENTER_WRAPPED_AMOUNT,
      }
    } else {
      return NOT_APPLICABLE
    }
  }, [
    chainId,
    inputCurrency,
    outputCurrency,
    account,
    weth,
    isWrapping,
    isUnwrapping,
    isWritePending,
    sufficientBalance,
    inputAmount,
    hasInputAmount,
    execute,
  ])
}
